export interface Colors {
  backgroundColor: string
  foregroundColor: string
}

export const gameraDomains = [
  'cfb',
  'fc',
  'mlb',
  'nba',
  'nfl',
  'nhl',
  'pga',
  'wnba',
] as const
export type GameraDomain = Uppercase<(typeof gameraDomains)[number]>

export type GameraToken = {
  text: string
  omitLeadingSpace: boolean
} & (
  | {
      type: 'general'
    }
  | {
      type: 'inferred'
    }
  | {
      type: 'entity'
      entity: GameraEntity
    }
)

export type GameraEntity = {
  display: string
  shortDisplay?: string
  domain: GameraDomain
  type: 'player' | 'teamFranchise' | 'teamSeason' | 'game' | 'player-ask'
  id?: string
  parameters?: Record<string, string>
  baseResourcePath?: string
}

export interface Value<T> {
  display: string
  value: T
}

export interface GameraGridColumn {
  rowItemKey: string
  title: string
  type: string
  tags?: {
    isReferencedInQuestion?: boolean
  }
}

export type GameraGridRow = Record<string, GameraGridCellValue<unknown>>

export interface GameraGridCellValue<T> extends Value<T> {
  imageUrl?: string
  entity?: GameraEntity
  tags?: {
    rankingOutOf?: number
    hasHighestValue?: boolean
  }
  meta?: {
    id?: number
    shortDisplay?: string
    position?: string
    injured?: boolean
    padding?: boolean
  }
}

export interface GameraGrid {
  name?: string
  columns: GameraGridColumn[]
  rows: GameraGridRow[]
  aggregations?: Record<string, GameraGridCellValue<unknown>>[]
}

export interface GameraParameter {
  values: {
    id: string
    display: string
  }[]
  selectedValueId: string
  order: number
}

export interface AdditionalQuestion {
  domain: string
  text: string
}

export interface GameraGalleryImage {
  url: string
  colors: Colors
  entity?: GameraEntity
}

export interface GameraPlayerReference {
  id: number
  usedName?: string
  firstName?: string
  lastName?: string
  entity: GameraEntity
  imageUrl: string
  colors: Colors
}

export interface GameraTeamReference {
  teamId: number
  seasonYear: number
  name: string
  nickname: string
  abbreviation: string
  logoImageUrl: string
  colors?: Colors
  entity: GameraEntity
}

export type TeamLine = {
  spread?: Value<number>
  spreadOdds?: Value<number>
  moneyline?: Value<number>
  moneylineOpen?: Value<number>
  spreadOpen?: Value<number>
  spreadOddsOpen?: Value<number>
}

export interface SportsbookLine {
  id:
    | 'consensus'
    | 'draftKings'
    | 'fanDuel'
    | 'mgm'
    | 'williamHill'
    | 'betMGM'
    | 'bovada'
  homeTeam?: TeamLine
  awayTeam?: TeamLine
  overUnder?: Value<number>
  overUnderOddsOver?: Value<number>
  overUnderOddsUnder?: Value<number>
  overUnderOpen?: Value<number>
}

export interface BettingOdds {
  sportsbooks?: SportsbookLine[]
}

export const primarySportsbook = (sportsbooks?: SportsbookLine[]) => {
  if (!sportsbooks) return undefined

  const orderMap = {
    mgm: 0,
    draftKings: 1,
    fanDuel: 2,
    consensus: 3,
    williamHill: 4,
    betMGM: 5,
    bovada: 6,
  } as const

  return sportsbooks.sort(
    (a, b) => (orderMap[a.id] ?? 5) - (orderMap[b.id] ?? 5),
  )[0]
}

export interface GameTeam {
  teamId: number
  score: number
  record: {
    wins: number
    losses: number
    ties?: number
    draws?: number
    overtimeLosses?: number
  }
  gameResult: string
}

export interface ScheduledGame {
  id: number
  gameId: number
  type: 'scheduled'
  season: Season
  gameTimestamp: string
  gameDate: string
  homeTeamId: number
  awayTeamId: number
  networkName?: string
  odds?: BettingOdds
}

export interface InProgressGame
  extends Omit<CompletedGame, 'type' | 'homeTeam' | 'awayTeam'> {
  type: 'inProgress'
  summary?: GameraToken[]
  homeTeam: Omit<GameTeam, 'record' | 'gameResult'>
  awayTeam: Omit<GameTeam, 'record' | 'gameResult'>
  gameStatus?:
    | 'fixture'
    | 'playing'
    | 'break'
    | 'suspended'
    | 'postponed'
    | 'cancelled'
    | 'awarded'
    | 'played'
  state?: { minute?: number; extraMinute?: number; period?: number }
}

export interface CompletedGame {
  id: number
  gameId: number
  type: 'completed'
  season: Season
  gameDate: string
  gameTimestamp?: string
  homeTeam: GameTeam
  awayTeam: GameTeam
}

export type Game = ScheduledGame | InProgressGame | CompletedGame

export const isScheduledGame = (game: Game): game is ScheduledGame =>
  game.type === 'scheduled'

export const isInProgressGame = (game: Game): game is InProgressGame =>
  game.type === 'inProgress'

export const isCompletedGame = (game: Game): game is CompletedGame =>
  game.type === 'completed'

export type SeasonType = 'regularSeason' | 'postseason'

export interface Season {
  type: SeasonType
  year: number
  yearDisplay?: string
}

export type StatModel<Keys extends string> = {
  [K in Keys]+?: Value<number>
}

export const getDisplay = <K extends string>(
  statModel: StatModel<K>,
  key: K,
) => {
  if (!statModel[key]) return ''
  return statModel[key].display
}

export const getValue = <K extends string>(statModel: StatModel<K>, key: K) => {
  if (!statModel[key]) return 0
  return statModel[key].value
}

export const shortenPosition = (x: string) => {
  switch (x) {
    case 'QUARTERBACK':
      return 'QB'
    case 'DEFENSIVE_LINEMAN':
      return 'DL'
    case 'DEFENSIVE_BACK':
      return 'DB'
    case 'KICKER':
      return 'K'
    case 'LINEBACKER':
      return 'LB'
    case 'LONG_SNAPPER':
      return 'LS'
    case 'OFFENSIVE_LINEMAN':
      return 'OL'
    case 'PUNTER':
      return 'P'
    case 'RUNNING_BACK':
      return 'RB'
    case 'TIGHT_END':
      return 'TE'
    case 'WIDE_RECEIVER':
      return 'WR'
    default:
      return x
  }
}

export const formatNbaPosition = (x: string) => {
  switch (x) {
    case 'CENTER':
      return 'C'
    case 'POINT_GUARD':
      return 'PG'
    case 'SHOOTING_GUARD':
      return 'SG'
    case 'POWER_FORWARD':
      return 'PF'
    case 'SMALL_FORWARD':
      return 'SF'
    case 'FORWARD':
      return 'F'
    case 'GUARD':
      return 'G'
    case 'GUARD_FORWARD':
      return 'G/F'
    case 'FORWARD_GUARD':
      return 'F/G'
    case 'FORWARD_CENTER':
      return 'F/C'
    case 'CENTER_FORWARD':
      return 'C/F'
    default:
      return x
  }
}

export const formatNhlPosition = (x: string) => {
  switch (x) {
    case 'GOALIE':
      return 'G'
    case 'CENTER':
      return 'C'
    case 'RIGHT_WING':
      return 'RW'
    case 'LEFT_WING':
      return 'LW'
    case 'DEFENSEMAN':
      return 'D'
    default:
      return x
  }
}
